import { useState, useMemo } from 'react';
import { Clock } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { EmptyState } from '../../components/common/EmptyState';
import { BOOKING_STATUSES, PAYMENT_STATUSES, TH, TD } from './config';
import type { BookingRow } from './hooks';

interface Props {
  bookings: BookingRow[];
  onSelect: (b: BookingRow) => void;
}

function fmt$(n: number): string { return `$${n.toLocaleString('en-CA', { minimumFractionDigits: 2 })}`; }
function fmtTime(t: string): string {
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

export function PastTab({ bookings, onSelect }: Props) {
  const [search, setSearch] = useState('');
  const [visible, setVisible] = useState(25);

  const past = useMemo(() => {
    const todayStr = new Date().toISOString().split('T')[0];
    let list = bookings.filter(b => b.date < todayStr || b.status === 'completed' || b.status === 'cancelled' || b.status === 'no_show');
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(b => (b.client_name ?? '').toLowerCase().includes(q) || (b.service_name ?? '').toLowerCase().includes(q));
    }
    return list.sort((a, b) => `${b.date}${b.start_time}`.localeCompare(`${a.date}${a.start_time}`));
  }, [bookings, search]);

  const groups = useMemo(() => {
    const map = new Map<string, BookingRow[]>();
    for (const b of past.slice(0, visible)) {
      const key = b.date.substring(0, 7);
      const arr = map.get(key) ?? [];
      arr.push(b);
      map.set(key, arr);
    }
    return Array.from(map.entries()).map(([month, rows]) => ({
      label: new Date(month + '-15').toLocaleDateString('en-CA', { month: 'long', year: 'numeric' }),
      rows,
    }));
  }, [past, visible]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search past bookings..."
          style={{ padding: '7px 12px', borderRadius: 6, border: `1px solid ${palette.g200}`, fontSize: 13, fontFamily: 'inherit', outline: 'none', width: 240 }} />
        <span style={{ fontSize: 13, color: palette.g400 }}>{past.length} booking{past.length !== 1 ? 's' : ''}</span>
      </div>

      {past.length === 0 ? (
        <EmptyState icon={<Clock size={22} />} title="No past bookings yet" subtitle="Completed bookings will appear here." />
      ) : (
        <div>
          {groups.map(g => (
            <div key={g.label} style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: palette.g400, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '8px 0', borderBottom: `1px solid ${palette.g150}` }}>
                {g.label} ({g.rows.length})
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={TH}>Date</th><th style={TH}>Time</th><th style={TH}>Client</th>
                  <th style={TH}>Service</th><th style={TH}>Status</th><th style={TH}>Payment</th>
                  <th style={{ ...TH, textAlign: 'right' }}>Amount</th>
                </tr></thead>
                <tbody>
                  {g.rows.map(b => {
                    const st = BOOKING_STATUSES[b.status] ?? BOOKING_STATUSES.completed;
                    const ps = PAYMENT_STATUSES[(b.payment_status as string) ?? 'unpaid'] ?? PAYMENT_STATUSES.unpaid;
                    return (
                      <tr key={b.id} onClick={() => onSelect(b)} style={{ cursor: 'pointer', transition: 'background 150ms ease' }}
                        onMouseEnter={e => (e.currentTarget.style.background = palette.g50)}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                        <td style={{ ...TD, fontWeight: 500 }}>{b.date}</td>
                        <td style={TD}>{fmtTime(b.start_time)}</td>
                        <td style={{ ...TD, fontWeight: 500 }}>{b.client_name ?? '—'}</td>
                        <td style={TD}>{b.service_name ?? '—'}</td>
                        <td style={TD}><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, background: st.bg, color: st.fg }}>{st.label}</span></td>
                        <td style={TD}><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, background: ps.bg, color: ps.fg }}>{ps.label}</span></td>
                        <td style={{ ...TD, textAlign: 'right' }}>{b.payment_amount ? fmt$(b.payment_amount) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
          {visible < past.length && (
            <button onClick={() => setVisible(v => v + 25)} style={{
              width: '100%', padding: '10px 0', borderRadius: 6, border: `1px solid ${palette.g200}`,
              background: palette.white, fontSize: 12.5, fontWeight: 500, color: palette.g600,
              cursor: 'pointer', fontFamily: 'inherit',
            }}>Load more</button>
          )}
        </div>
      )}
    </div>
  );
}
